import { useCallback, useEffect, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  CircleAlert,
  Eye,
  LoaderCircle,
  RefreshCw,
  WalletCards,
} from "lucide-react";

import { getSquareBookings, getSquareOverview } from "./api";
import type { SquareBooking, SquareOverview, SquareResourceSummary } from "./types";

const resources: Array<{
  key: keyof Pick<
    SquareOverview,
    | "locations"
    | "catalog"
    | "customers"
    | "payments"
    | "orders"
    | "invoices"
    | "payouts"
    | "refunds"
    | "disputes"
    | "teamMembers"
    | "bookings"
  >;
  label: string;
  description: string;
}> = [
  { key: "locations", label: "Locations", description: "Business locations" },
  { key: "catalog", label: "Catalog", description: "Items and services" },
  { key: "customers", label: "Customers", description: "Square customer directory" },
  { key: "payments", label: "Payments", description: "Recent payment records" },
  { key: "orders", label: "Orders", description: "Sales and returns" },
  { key: "invoices", label: "Invoices", description: "Invoice collection status" },
  { key: "payouts", label: "Payouts", description: "Deposits and withdrawals" },
  { key: "refunds", label: "Refunds", description: "Refund records" },
  { key: "disputes", label: "Disputes", description: "Chargeback records" },
  { key: "teamMembers", label: "Team", description: "Team member records" },
  { key: "bookings", label: "Bookings", description: "Square Appointments" },
];

export function SquarePage() {
  const [overview, setOverview] = useState<SquareOverview>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [bookings, setBookings] = useState<SquareBooking[]>([]);
  const [bookingsState, setBookingsState] = useState<SquareOverview["bookings"]["state"]>();
  const [bookingsError, setBookingsError] = useState<string | null>(null);
  const [bookingsLoading, setBookingsLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setOverview(await getSquareOverview());
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  const loadBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const page = await getSquareBookings({ limit: 25 });
      setBookings(page.items);
      setBookingsState(page.state);
      setBookingsError(page.errorCode);
    } catch {
      setBookingsState("error");
      setBookingsError("REQUEST_FAILED");
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  useEffect(() => void loadBookings(), [loadBookings]);

  return (
    <div className="page-content square-page">
      <header className="page-intro integration-intro">
        <span className="eyebrow lime">Payments &amp; commerce</span>
        <h1>Square, visible without taking control.</h1>
        <p>
          A read-only view of the Square account. TLC can review locations, catalog, customer,
          payment, order, team, and booking availability without changing anything in Square.
        </p>
      </header>

      {failed ? (
        <div className="load-error" role="alert">
          We couldn’t refresh the Square connection.
          <button onClick={() => void load()}>Try again</button>
        </div>
      ) : null}

      <section className="square-connection-card" aria-live="polite">
        <div className="square-connection-mark">
          <WalletCards />
        </div>
        <div className="square-connection-copy">
          <span className={`square-state ${overview?.connected ? "connected" : ""}`}>
            {loading ? "Checking connection" : overview?.connected ? "Connected" : "Not connected"}
          </span>
          <h2>Square account</h2>
          <p>
            {overview?.enabled
              ? `Production account · API ${overview.apiVersion}`
              : "The read-only connection is waiting for server-side credentials."}
          </p>
        </div>
        <div className="square-connection-facts">
          <Fact label="Mode" value={overview?.environment ?? "—"} />
          <Fact label="Access" value="Read-only" />
          <Fact label="Last checked" value={formatDate(overview?.checkedAt)} />
        </div>
        <button
          className="text-button square-refresh"
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? <LoaderCircle className="spin" /> : <RefreshCw />} Refresh
        </button>
      </section>

      <section className="square-summary-grid" aria-label="Square connection summary">
        <SummaryCard label="Customers" value={displayCount(overview?.customers)} />
        <SummaryCard label="Payments" value={displayCount(overview?.payments)} />
        <SummaryCard label="Payouts" value={displayCount(overview?.payouts)} />
      </section>

      <section className="content-card square-bookings-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Appointments · read-only</span>
            <h2>Square appointments</h2>
            <p className="section-caption">
              Existing Square Appointments can be reviewed here when the account is onboarded to the
              Bookings API. No appointment is created, edited, or cancelled by TLC Operations.
            </p>
          </div>
          <button
            className="secondary-button small-action"
            onClick={() => void loadBookings()}
            disabled={bookingsLoading}
          >
            {bookingsLoading ? <LoaderCircle className="spin" /> : <RefreshCw />} Refresh
          </button>
        </div>
        {bookingsState === "available" && bookings.length ? (
          <SquareBookingsTable bookings={bookings} />
        ) : bookingsLoading ? (
          <div className="table-state">
            <LoaderCircle className="spin" /> Checking Square appointments…
          </div>
        ) : bookingsState === "not_available" ? (
          <div className="square-bookings-empty">
            <CalendarDays />
            <strong>Square Appointments is not enabled for this account.</strong>
            <span>
              The connection is healthy and remains read-only. After Square onboards the merchant to
              Bookings, this panel will begin listing existing appointments automatically.
            </span>
          </div>
        ) : bookingsState === "available" ? (
          <div className="table-state">No Square appointments were returned.</div>
        ) : (
          <div className="load-error" role="alert">
            We couldn’t read Square appointments{bookingsError ? ` (${bookingsError})` : ""}.
            <button onClick={() => void loadBookings()}>Try again</button>
          </div>
        )}
      </section>

      <section className="content-card square-resources-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Read-only coverage</span>
            <h2>Available Square data</h2>
          </div>
          <span className="square-read-only-badge">
            <Eye /> No write actions
          </span>
        </div>
        <div className="square-resource-grid">
          {resources.map((resource) => (
            <ResourceCard
              key={resource.key}
              label={resource.label}
              description={resource.description}
              summary={overview?.[resource.key]}
            />
          ))}
        </div>
      </section>

      <aside className="square-guardrail">
        <CheckCircle2 />
        <div>
          <strong>Protected by design</strong>
          <span>
            This integration only calls Square list, retrieve, and search endpoints. It never
            creates payments, edits catalog items, changes customers, or modifies appointments.
          </span>
        </div>
      </aside>
    </div>
  );
}

function SquareBookingsTable({ bookings }: { bookings: SquareBooking[] }) {
  return (
    <div className="table-wrap">
      <table className="mobile-card-table">
        <thead>
          <tr>
            <th>Appointment</th>
            <th>Status</th>
            <th>Customer</th>
            <th>Starts</th>
            <th>Ends</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking) => (
            <tr key={booking.id}>
              <td data-label="Appointment">
                <strong>{booking.id}</strong>
                <span>{booking.locationId ?? "Location not provided"}</span>
              </td>
              <td data-label="Status">{booking.status ?? "—"}</td>
              <td data-label="Customer">{booking.customerId ?? "—"}</td>
              <td data-label="Starts">{formatDate(booking.startAt ?? undefined)}</td>
              <td data-label="Ends">{formatDate(booking.endAt ?? undefined)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResourceCard({
  label,
  description,
  summary,
}: {
  label: string;
  description: string;
  summary?: SquareResourceSummary;
}) {
  const state = summary?.state ?? "not_configured";
  return (
    <article className="square-resource-card">
      <div className={`square-resource-icon square-resource-${state}`}>
        {state === "available" ? <CheckCircle2 /> : <CircleAlert />}
      </div>
      <div className="square-resource-copy">
        <h3>{label}</h3>
        <p>{description}</p>
      </div>
      <strong className="square-resource-count">{displayCount(summary)}</strong>
      <span className={`square-resource-state square-resource-state-${state}`}>
        {stateLabel(state)}
      </span>
      {summary?.errorCode ? <small>{summary.errorCode}</small> : null}
    </article>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <article className="metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>read-only snapshot</small>
    </article>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function displayCount(summary?: SquareResourceSummary) {
  if (!summary || summary.state !== "available") return "—";
  if (summary.count != null) return summary.count.toLocaleString("en-US");
  if (summary.sampleCount != null) return `${summary.sampleCount}${summary.hasMore ? "+" : ""}`;
  return "0";
}

function stateLabel(state: SquareResourceSummary["state"]) {
  if (state === "available") return "Available";
  if (state === "permission_required") return "Permission needed";
  if (state === "not_available") return "Not enabled";
  if (state === "error") return "Unavailable";
  return "Not configured";
}

function formatDate(value?: string) {
  if (!value) return "Not checked yet";
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}
